import { useState, useEffect, useRef } from 'react';
import { Box, Slider } from '@mui/material';
import Opacity from '@mui/icons-material/Opacity';
import CustomInput from '../CustomInput';

interface OpacityControlProps {
  value: number; // Current opacity value (0-100)
  onChange: (opacity: number) => void; // Callback when opacity changes (debounced)
  debounceDelay?: number; // debounce delay in milliseconds (default: 300)
  min?: number; // minimum opacity value (default: 0)
  'data-testid'?: string;
}

/**
 * OpacityControl
 *
 * A reusable component for controlling layer opacity with a slider and numeric input.
 * Changes are debounced to avoid excessive updates.
 *
 * @param {number} value - Current opacity value (0-100)
 * @param {Function} onChange - Callback when opacity changes
 * @param {number} debounceDelay - Debounce delay in milliseconds (default: 300)
 * @param {number} min - Minimum opacity value (default: 0)
 * @returns {JSX.Element} The rendered opacity slider component
 */
export default function OpacityControl({
  value,
  onChange,
  debounceDelay = 300,
  min = 0,
  'data-testid': dataTestId,
}: OpacityControlProps) {
  // Use local state for opacity to avoid immediate dispatch on slider change
  const [localOpacity, setLocalOpacity] = useState<number | ''>(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync external value changes to local state
  useEffect(() => {
    setLocalOpacity(value); // eslint-disable-line react-hooks/set-state-in-effect
  }, [value]);

  // Sync localOpacity to parent with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // Only dispatch if the value is valid and within bounds
      if (
        localOpacity !== '' &&
        Number(localOpacity) >= min &&
        Number(localOpacity) <= 100
      ) {
        onChange(Number(localOpacity));
      } else if (localOpacity !== '') {
        // Revert invalid value back to the last valid value from parent
        setLocalOpacity(value);
      }
    }, debounceDelay);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localOpacity, onChange, debounceDelay, min, value]);

  // Handle local opacity state updates from input
  const handleOpacityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    // Allow empty string (for clearing input)
    if (input === '') {
      setLocalOpacity('');
      return;
    }

    // Only allow digits 0-9 (no letters, +, -, e, etc.)
    if (!/^\d{1,3}$/.test(input)) {
      return;
    }

    const numericValue = Number(input);
    // Allow intermediate values during typing (e.g., "2" when typing "22" with min=10)
    // Max validation only - min validation happens on debounce/onChange
    if (numericValue > 100) {
      return;
    }
    // Accept any valid 1-3 digit number, bounds validation happens on debounce
    setLocalOpacity(numericValue);
  };

  return (
    <Box
      id="opacity-container"
      sx={{
        display: 'flex',
        width: '95%',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <Opacity color="primary" />
      <Slider
        data-testid={dataTestId || 'opacity-slider'}
        value={localOpacity || 0}
        min={min}
        max={100}
        aria-label="opacity"
        onChange={(_, val) => setLocalOpacity(val as number)}
      />
      <Box sx={{ flexShrink: 0 }}>
        <CustomInput
          value={localOpacity}
          onChange={handleOpacityChange}
          width="72px"
          suffix="%"
          data-testid="opacity-picker"
          aria-label="Percent opacity"
        />
      </Box>
    </Box>
  );
}
